import React, { Fragment, useMemo } from 'react';
import BreadCrumbs from '../../components/core/BreadCrumbs';
import { useGetCase } from '../../hooks/cases/useGetCase';
import { Button, Col, Empty, Row, Table, Typography } from 'antd';
import { APP_TERMS } from '../../app-terms';
import BasicCard from '../../components/core/Cards/BasicCard';
import BasicDivider from '../../components/core/Divider/BasicDivider';
import InfoItem from '../../components/core/Cards/InfoItem';
import {
  getPatientDetailInfoItems,
  getStudyPurposeInfoItems,
} from '../../utils/case';
import CaseFileDownloadLink from '../../components/core/File/CaseFileDownloadLink';
import { useUserLocale } from '../../hooks/useLocale';
import { formatDate } from '../../utils/date';
import { CASE_STATUS_COMPLETED } from './CaseConstants';
import { useNavigate } from 'react-router-dom';
import CaseViewLoader from '../../components/core/Loaders/CaseViewLoader';

const { Text } = Typography;
const BREAD_CRUMBS = [
  { label: 'Case', to: '/user/case/created' },
  { label: 'View', to: '/user/case/view/:id' },
];

const SERVICES_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'title',
    render: (value) => {
      return <Text style={{ textWrap: 'wrap' }}>{value}</Text>;
    },
    onCell: () => {
      return { colSpan: 2 };
    },
    colSpan: 2,
    align: 'left',
  },
  {
    title: 'Price',
    dataIndex: 'amount',
    align: 'right',
    ellipsis: true,
  },
  {
    title: 'Rush Fee',
    dataIndex: 'rushFee',
    align: 'right',
    ellipsis: true,
  },
];

const ViewCase = () => {
  const navigate = useNavigate();
  const { userLocale } = useUserLocale();
  const { data, loading } = useGetCase();
  const patientDetails = useMemo(
    () => getPatientDetailInfoItems(data, userLocale),
    [data]
  );
  const studyPurpose = useMemo(() => getStudyPurposeInfoItems(data), [data]);
  const services = useMemo(() => data?.services, [data]);
  const files = useMemo(() => data?.files, [data]);
  const reportFiles = useMemo(
    () =>
      data?.cmsFiles?.length ? data?.cmsFiles : data?.radiologistFiles || [],
    [data]
  );

  const FILES_COLUMNS = useMemo(
    () => [
      {
        title: 'File Name',
        dataIndex: 'fileName',
        align: 'left',
        render: (value, record) => {
          return (
            <CaseFileDownloadLink
              caseId={data?.caseId}
              fileName={value}
              objectKey={record?.objectKey}
            />
          );
        },
        onCell: () => {
          return { colSpan: 2 };
        },
        colSpan: 2,
      },
      {
        title: 'Size',
        dataIndex: 'fileSize',
        align: 'right',
        ellipsis: true,
        render: (value) => {
          const KB = parseInt(value / 1024) || '-';
          return <Text style={{ textWrap: 'wrap' }}>{KB} KB</Text>;
        },
      },
    ],
    [data]
  );

  return (
    <>
      <BreadCrumbs items={BREAD_CRUMBS} />
      <div className="row card">
        <div className="card invoices-tabs-card">
          <div className="card-body card-body pt-0 pb-0">
            {loading ? (
              <CaseViewLoader />
            ) : (
              <>
                <div className="invoices-main-tabs">
                  <div className="row align-items-center">
                    <div className="wizard mt-4">
                      <Row justify={'space-between'}>
                        <Col xs={24} sm={24} md={8} className="text-start">
                          <b>Case ID: </b>
                          {data?.caseId}
                        </Col>
                        <Col xs={24} sm={24} md={8} className="text-center">
                          <b>Date: </b>
                          {data?.createdAt &&
                            formatDate(
                              data?.createdAt,
                              APP_TERMS.date_1_format,
                              userLocale
                            )}
                        </Col>
                        <Col xs={24} sm={24} md={8} className="text-end">
                          <b>Patient Name: </b>
                          {`${data?.patient?.firstName} ${data?.patient?.lastName}`}
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
                <Row gutter={[16, 16]} className="mt-3">
                  <Col sm={24} md={12}>
                    <BasicCard
                      size="small"
                      style={{ height: '100%' }}
                      title={`${APP_TERMS.generic_patients} ${APP_TERMS.generic_details}`}
                    >
                      {patientDetails?.map((details, index) => {
                        return (
                          <Fragment key={details?.label}>
                            <InfoItem
                              label={details?.label}
                              value={details?.value}
                            />
                            {index < patientDetails?.length - 1 ? (
                              <BasicDivider />
                            ) : null}
                          </Fragment>
                        );
                      })}
                    </BasicCard>
                  </Col>
                  <Col sm={24} md={12}>
                    <BasicCard
                      size="small"
                      style={{ height: '100%' }}
                      title={`${APP_TERMS.generic_study} ${APP_TERMS.generic_purpose}`}
                    >
                      {studyPurpose?.map((details, index) => {
                        return (
                          <Fragment key={details?.label}>
                            <InfoItem
                              label={details?.label}
                              value={details?.value}
                              type={'vertical'}
                            />
                            {index < studyPurpose?.length - 1 ? (
                              <BasicDivider />
                            ) : null}
                          </Fragment>
                        );
                      })}
                    </BasicCard>
                  </Col>
                  <Col sm={24} md={12}>
                    <BasicCard
                      size="small"
                      style={{ height: '100%' }}
                      title={APP_TERMS.generic_services}
                    >
                      <Table
                        columns={SERVICES_COLUMNS}
                        dataSource={services}
                        pagination={false}
                        rowKey={(record) => record?.csId}
                        summary={(pageData) => {
                          let totalAmount = 0;
                          let totalRushFee = 0;

                          pageData.forEach(({ amount, rushFee }) => {
                            totalAmount += amount;
                            totalRushFee += rushFee;
                          });

                          return (
                            <>
                              <Table.Summary.Row className="bg-light">
                                <Table.Summary.Cell index={0} colSpan={2}>
                                  Sub Total
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right">
                                  <Text type="danger">{totalAmount}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4} align="right">
                                  <Text>{totalRushFee}</Text>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                              <Table.Summary.Row
                                className="bg-light"
                                style={{ fontWeight: 'bolder' }}
                              >
                                <Table.Summary.Cell index={0} colSpan={2}>
                                  Total
                                </Table.Summary.Cell>
                                <Table.Summary.Cell
                                  index={1}
                                  colSpan={2}
                                  align="right"
                                >
                                  <Text type="danger">
                                    {totalAmount + totalRushFee}
                                  </Text>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </>
                          );
                        }}
                      />
                    </BasicCard>
                  </Col>
                  <Col sm={24} md={12}>
                    <BasicCard
                      size="small"
                      style={{ height: '100%' }}
                      title={APP_TERMS.generic_files}
                    >
                      <Table
                        columns={FILES_COLUMNS}
                        dataSource={files}
                        pagination={false}
                        rowKey={(record) => record?.fileId}
                      />
                    </BasicCard>
                  </Col>
                  {data?.status === CASE_STATUS_COMPLETED ? (
                    <Col sm={24} md={12}>
                      <BasicCard
                        size="small"
                        style={{ height: '100%' }}
                        title={APP_TERMS.case_result_summary_title}
                      >
                        {
                          <InfoItem
                            value={
                              data?.caseResultSummary ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: data?.caseResultSummary,
                                  }}
                                ></div>
                              ) : (
                                <Empty />
                              )
                            }
                            type={'vertical'}
                          />
                        }
                      </BasicCard>
                    </Col>
                  ) : null}
                  <Col sm={24} md={12}>
                    <BasicCard
                      size="small"
                      style={{ height: '100%' }}
                      title={APP_TERMS.generic_report}
                    >
                      <Table
                        columns={FILES_COLUMNS}
                        dataSource={reportFiles}
                        pagination={false}
                        rowKey={(record) => record?.fileId}
                      />
                    </BasicCard>
                  </Col>
                  <Col sm={24} md={12}>
                    <BasicCard
                      size="small"
                      style={{ height: '100%' }}
                      title={APP_TERMS.generic_comments}
                    >
                      {
                        <InfoItem
                          value={data?.internalComments ?? <Empty />}
                          type={'vertical'}
                        />
                      }
                    </BasicCard>
                  </Col>
                  <Col span={24}>
                    <Button htmlType="button" onClick={() => navigate(-1)}>
                      Back
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewCase;
